import React, { useMemo, useState } from "react";
import Select from "react-select";
import moment from "moment";

const ChevronDown = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M4 6l4 4 4-4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Cross = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
    <path
      d="M2 2l6 6M8 2L2 8"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

const FilterIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M2 3h12M4 8h8M6 13h4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const PRESETS = [
  { id: "7d",  label: "7d",  days: 7 },
  { id: "30d", label: "30d", days: 30 },
  { id: "90d", label: "90d", days: 90 },
  { id: "ytd", label: "YTD" },
  { id: "1y",  label: "1y",  days: 365 },
];

const computePresetRange = (preset) => {
  const today = moment().format("YYYY-MM-DD");
  if (preset.id === "ytd") {
    return { start: moment().startOf("year").format("YYYY-MM-DD"), end: today };
  }
  return {
    start: moment().subtract(preset.days, "days").format("YYYY-MM-DD"),
    end: today,
  };
};

const detectPreset = (start, end) => {
  if (!start || !end) return null;
  for (const p of PRESETS) {
    const r = computePresetRange(p);
    if (r.start === start && r.end === end) return p.id;
  }
  return null;
};

const formatDateChip = (d) =>
  d ? moment(d, "YYYY-MM-DD").format("MMM D, YYYY") : "";

const FilterBar = ({
  showManager,
  showManagerChip = true,
  managers,
  managerValue,
  onManagerChange,
  properties,
  propertyValue,
  onPropertyChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  onClear,
  canClear,
}) => {
  const [open, setOpen] = useState(false);

  const activePreset = useMemo(
    () => detectPreset(startDate, endDate),
    [startDate, endDate]
  );

  const applyPreset = (preset) => {
    const { start, end } = computePresetRange(preset);
    onStartDateChange(start);
    onEndDateChange(end);
  };

  const chips = useMemo(() => {
    const list = [];
    if (showManagerChip && managerValue && managerValue.value) {
      list.push({
        key: "Manager",
        value: managerValue.label || managerValue.value,
        clear: () => onManagerChange({ label: "", value: "" }),
      });
    }
    if (propertyValue && propertyValue.value) {
      list.push({
        key: "Property",
        value: propertyValue.label || propertyValue.value,
        clear: () => onPropertyChange(null),
      });
    }
    if (startDate || endDate) {
      list.push({
        key: "Date",
        value:
          activePreset
            ? PRESETS.find((p) => p.id === activePreset).label
            : `${formatDateChip(startDate) || "—"} → ${
                formatDateChip(endDate) || "Today"
              }`,
        clear: () => {
          onStartDateChange("");
          onEndDateChange("");
        },
      });
    }
    return list;
  }, [
    showManagerChip,
    managerValue,
    propertyValue,
    startDate,
    endDate,
    activePreset,
    onManagerChange,
    onPropertyChange,
    onStartDateChange,
    onEndDateChange,
  ]);

  return (
    <div
      className="rdc-filterbar"
      role="group"
      aria-label="Dashboard filters"
    >
      <div className="rdc-filterbar__bar">
        <div className="rdc-filterbar__left">
          <span className="rdc-filterbar__title">
            <FilterIcon />
            Filters
            {chips.length > 0 && (
              <span
                className="rdc-filterbar__count"
                aria-label={`${chips.length} active`}
              >
                {chips.length}
              </span>
            )}
          </span>

          {chips.length === 0 ? (
            <span className="rdc-filterbar__hint">
              Showing data for all time
            </span>
          ) : (
            <div className="rdc-filterbar__chips">
              {chips.map((c) => (
                <span key={c.key} className="rdc-filterbar__chip">
                  <span className="rdc-filterbar__chip-key">{c.key}:</span>
                  <span className="rdc-filterbar__chip-value">{c.value}</span>
                  <button
                    type="button"
                    className="rdc-filterbar__chip-remove"
                    onClick={c.clear}
                    aria-label={`Remove ${c.key} filter`}
                  >
                    <Cross />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="rdc-filterbar__right">
          <div
            className="rdc-filterbar__preset-group"
            role="group"
            aria-label="Date presets"
          >
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={
                  "rdc-filterbar__preset" +
                  (activePreset === p.id
                    ? " rdc-filterbar__preset--active"
                    : "")
                }
                onClick={() => applyPreset(p)}
                aria-pressed={activePreset === p.id}
              >
                {p.label}
              </button>
            ))}
          </div>

          <button
            type="button"
            className="rdc-filterbar__clear"
            onClick={onClear}
            disabled={!canClear}
          >
            Clear
          </button>

          <button
            type="button"
            className={
              "rdc-filterbar__toggle" +
              (open ? " rdc-filterbar__toggle--open" : "")
            }
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="rdc-filterbar-panel"
          >
            {open ? "Less" : "More"}
            <span className="rdc-filterbar__toggle__chevron">
              <ChevronDown />
            </span>
          </button>
        </div>
      </div>

      {open && (
        <div id="rdc-filterbar-panel" className="rdc-filterbar__panel">
          {showManager && (
            <div className="rdc-filterbar__field">
              <label
                className="rdc-filterbar__label"
                htmlFor="rdc-manager-select"
              >
                Manager
              </label>
              <Select
                inputId="rdc-manager-select"
                classNamePrefix="rdc-select"
                className="rdc-filterbar__select"
                placeholder="All managers"
                value={
                  managerValue && managerValue.value ? managerValue : null
                }
                options={managers}
                isClearable
                onChange={(v) =>
                  onManagerChange(v || { label: "", value: "" })
                }
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </div>
          )}

          <div className="rdc-filterbar__field">
            <label
              className="rdc-filterbar__label"
              htmlFor="rdc-property-select"
            >
              Property
            </label>
            <Select
              inputId="rdc-property-select"
              classNamePrefix="rdc-select"
              className="rdc-filterbar__select"
              placeholder="All properties"
              value={
                propertyValue && propertyValue.value ? propertyValue : null
              }
              options={properties}
              isClearable
              onChange={(v) => onPropertyChange(v || null)}
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>

          <div className="rdc-filterbar__field">
            <label className="rdc-filterbar__label" htmlFor="rdc-start-date">
              Start date
            </label>
            <input
              id="rdc-start-date"
              type="date"
              className="rdc-filterbar__input"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
            />
          </div>

          <div className="rdc-filterbar__field">
            <label className="rdc-filterbar__label" htmlFor="rdc-end-date">
              End date
            </label>
            <input
              id="rdc-end-date"
              type="date"
              className="rdc-filterbar__input"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterBar;
